import { Navigate, useLocation } from "react-router-dom";

import {
  HCBB_COST_ESTIMATOR_RESULT_PATH,
  readHcbbFacilityState,
  readHcbbHighlightState,
} from "@/lib/hcbb-cost-estimator-utils";
import { useHcbbCommittedSearch } from "@/hooks/use-hcbb-cost-estimator";
import { HcbbBackButton } from "@/pages/components/hcbb-cost-estimator/hcbb-back-button";
import { HcbbFacilityProviders } from "@/pages/components/hcbb-cost-estimator/hcbb-facility-providers";
import { HcbbFacilitySummary } from "@/pages/components/hcbb-cost-estimator/hcbb-facility-summary";
import PageContainer from "@/pages/containers/page-container";

/**
 * One facility from the cost estimate, opened from its result row.
 *
 * Both the facility and the results page's own priced benchmark (for the
 * doctor list's `majorGroup`) travel in router state, so nothing on this
 * screen re-fetches the estimate: a URL opened without a facility (a refresh,
 * a shared link) has no row to show and returns to the results, which the
 * query string still describes. Back is a history step: the results are the
 * only way in, and stepping back is what leaves them exactly as the member
 * had them. The doctor list below the summary is the one thing this screen
 * does fetch, against the same committed search the query string still
 * carries.
 */
export const HcbbFacilityDetailPage = () => {
  const { state, search } = useLocation();
  const facility = readHcbbFacilityState(state);
  const highlight = readHcbbHighlightState(state);
  const committedSearch = useHcbbCommittedSearch();

  if (!facility) {
    return (
      <Navigate
        to={{ pathname: HCBB_COST_ESTIMATOR_RESULT_PATH, search }}
        replace
      />
    );
  }

  return (
    <PageContainer
      headerLeftContent={
        <HcbbBackButton label="Back to cost estimate results" />
      }
      title="Facility Details"
    >
      <div className="space-y-6 p-6">
        <HcbbFacilitySummary facility={facility} />
        <HcbbFacilityProviders
          search={committedSearch}
          facility={facility}
          highlight={highlight}
        />
      </div>
    </PageContainer>
  );
};
